import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Upload, CheckCircle, Building2, User, Wrench, MapPin, FileText, Eye, EyeOff, Camera, X, Palette, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  partenaire: {
    title: 'Partenaire',
    subtitle: 'Marque Blanche',
    color: 'purple',
    requiresCompany: false,
    showPartnerModel: true,
    showVille: true,
  },
  affilie: {
    title: 'Affilié',
    subtitle: 'Apporteur d\'affaires - 15% commission',
    color: 'gold',
    requiresCompany: false,
    showVille: true,
  },
  chiffreur: {
    title: 'Chiffreur',
    subtitle: 'Devis & Chiffrage - 50€/dossier',
    color: 'emerald',
    requiresCompany: false,
  },
  analyste: {
    title: 'Analyste',
    subtitle: 'Audit & Analyse - 30€/dossier',
    color: 'blue',
    requiresCompany: false,
  },
  artisan: {
    title: 'Artisan / Société Travaux',
    subtitle: 'Recevez des chantiers qualifiés',
    color: 'orange',
    requiresCompany: true,
    showMetiers: true,
    showZone: true,
    showDocuments: true,
  },
};

export default function ReseauInscriptionPage() {
  const navigate = useNavigate();
  const { type } = useParams();
  const [searchParams] = useSearchParams();
  const config = TYPE_CONFIG[type] || TYPE_CONFIG.affilie;
  // Code affilié passé en paramètre URL (?ref=CODE)
  const referredByAffiliate = searchParams.get('ref') || '';
  // Pays pré-sélectionné depuis la page précédente (?country=algerie)
  const countryFromUrl = searchParams.get('country') || 'france';
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [metiersList, setMetiersList] = useState([]);
  const [alreadyExists, setAlreadyExists] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  
  // Logo upload states
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  
  // Predefined brand colors
  const brandColors = [
    { name: 'Violet', value: '#7C3AED' },
    { name: 'Bleu', value: '#3B82F6' },
    { name: 'Vert', value: '#10B981' },
    { name: 'Orange', value: '#F59E0B' },
    { name: 'Rouge', value: '#EF4444' },
    { name: 'Rose', value: '#EC4899' },
    { name: 'Cyan', value: '#06B6D4' },
    { name: 'Noir', value: '#1F2937' },
  ];
  
  const [formData, setFormData] = useState({
    member_type: type,
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    password: '',
    is_company: config.requiresCompany,
    company_name: '',
    siret: '',
    address: '',
    city: '',
    postal_code: '',
    // Artisan
    metiers: [],
    metier_autre: '',
    zone_ville: '',
    zone_rayon_km: 50,
    // Agence
    activite: '',
    // Partenaire
    partner_model: 'page_blanche',
    logo_url: '',
    subdomain: '',
    primary_color: '#7C3AED',
    country: countryFromUrl,
  });

  // Charger la liste des métiers
  useEffect(() => {
    if (type === 'artisan') {
      fetch(`${API}/api/reseau/metiers`)
        .then(res => res.json())
        .then(data => setMetiersList(data.metiers || []))
        .catch(() => setMetiersList(['Maçon', 'Électricien', 'Plombier', 'Peintre', 'Autre']));
    }
  }, [type]);

  // Pré-remplir avec les infos saisies à l'étape 1 (email/nom/prénom)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('reseau_lead_prefill');
      if (raw) {
        const p = JSON.parse(raw);
        setFormData(prev => ({
          ...prev,
          email: p.email || prev.email,
          nom: p.nom || prev.nom,
          prenom: p.prenom || prev.prenom,
        }));
      }
    } catch (e) { /* ignore */ }
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleMetierToggle = (metier) => {
    setFormData(prev => ({
      ...prev,
      metiers: prev.metiers.includes(metier)
        ? prev.metiers.filter(m => m !== metier)
        : [...prev.metiers, metier]
    }));
  };

  // Handle logo file selection
  const handleLogoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez JPG, PNG, GIF, WebP ou SVG.');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 5MB)');
      return;
    }
    
    setLogoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => setLogoPreview(e.target.result);
    reader.readAsDataURL(file);
  };
  
  // Upload logo to server
  const uploadLogo = async () => {
    if (!logoFile) return null;
    
    setUploadingLogo(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', logoFile);
      
      const response = await fetch(`${API}/api/upload/logo`, {
        method: 'POST',
        body: formDataUpload,
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur upload');
      }
      
      const data = await response.json();
      return data.logo_url;
    } catch (error) {
      toast.error(`Erreur upload logo: ${error.message}`);
      return null;
    } finally {
      setUploadingLogo(false);
    }
  };
  
  // Remove selected logo
  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    handleChange('logo_url', '');
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Upload logo first if file is selected
      let finalLogoUrl = formData.logo_url;
      if (logoFile) {
        const uploadedUrl = await uploadLogo();
        if (uploadedUrl) {
          finalLogoUrl = uploadedUrl;
        }
      }
      
      // Prepare data with uploaded logo URL
      const dataToSubmit = {
        ...formData,
        logo_url: finalLogoUrl,
        ...(referredByAffiliate ? { referred_by_affiliate: referredByAffiliate } : {})
      };

      const response = await fetch(`${API}/api/reseau/inscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSubmit),
      });

      const data = await response.json();

      if (!response.ok) {
        // Cas spécial: email déjà inscrit → proposer connexion / mot de passe oublié
        if (response.status === 400 && (data.detail || '').toLowerCase().includes('déjà inscrit')) {
          setAlreadyExists(true);
          toast.error(data.detail);
          return;
        }
        throw new Error(data.detail || 'Erreur lors de l\'inscription');
      }

      // For Page Blanche partners, redirect to payment
      if (type === 'partenaire' && formData.partner_model === 'page_blanche') {
        toast.success('Redirection vers le paiement...');
        
        // Create checkout session
        const checkoutResponse = await fetch(`${API}/api/partenaire/subscription/checkout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            member_id: data.id,
            origin_url: window.location.origin
          }),
        });

        const checkoutData = await checkoutResponse.json();

        if (!checkoutResponse.ok) {
          throw new Error(checkoutData.detail || 'Erreur lors de la création du paiement');
        }

        // Redirect to Stripe checkout
        window.location.href = checkoutData.checkout_url;
        return; // Don't set success state, we're redirecting
      }

      setSuccess(true);
      toast.success('Inscription enregistrée !');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!formData.email) {
      toast.error('Veuillez renseigner votre email');
      return;
    }
    setForgotLoading(true);
    try {
      const response = await fetch(`${API}/api/reseau/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, member_type: type }),
      });
      await response.json();
      setForgotSent(true);
      toast.success('Si un compte existe, un nouveau mot de passe a été envoyé par email.');
    } catch (error) {
      toast.error('Une erreur est survenue. Réessayez plus tard.');
    } finally {
      setForgotLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center p-6">
        <Card className="max-w-md w-full border-0 shadow-2xl bg-white/10 backdrop-blur">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-400" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-white mb-4">
              Inscription enregistrée !
            </h2>
            <p className="text-white/70 mb-6">
              Votre demande est à l'étude. Vous recevrez un email de confirmation dans les 48h.
              <br /><span className="text-amber-400 text-sm">⚠️ Pensez à vérifier vos spams si vous ne recevez rien.</span>
            </p>
            <Button 
              onClick={() => navigate('/reseau')}
              className="bg-gold hover:bg-gold/90 text-black"
            >
              Retour
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (alreadyExists) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center p-6">
        <Card className="max-w-md w-full border-0 shadow-2xl bg-white/10 backdrop-blur" data-testid="already-exists-panel">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
              <User className="w-10 h-10 text-amber-400" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-white mb-3">
              Vous avez déjà un compte
            </h2>
            <p className="text-white/70 mb-2">
              L'email <strong className="text-white">{formData.email}</strong> est déjà inscrit comme <strong className="text-amber-400">{config.title}</strong>.
            </p>
            {!forgotSent ? (
              <>
                <p className="text-white/60 text-sm mb-6">
                  Connectez-vous à votre espace, ou réinitialisez votre mot de passe si vous l'avez oublié.
                </p>
                <div className="space-y-3">
                  <Button
                    onClick={() => navigate(`/reseau/${type}/login`)}
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-6"
                    data-testid="already-exists-login-btn"
                  >
                    Se connecter
                  </Button>
                  <Button
                    onClick={handleForgotPassword}
                    disabled={forgotLoading}
                    variant="outline"
                    className="w-full border-white/20 text-white hover:bg-white/10 py-6"
                    data-testid="already-exists-forgot-btn"
                  >
                    {forgotLoading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi en cours...</>
                    ) : (
                      'Mot de passe oublié ?'
                    )}
                  </Button>
                  <button
                    onClick={() => setAlreadyExists(false)}
                    className="text-white/50 hover:text-white text-sm underline"
                    data-testid="already-exists-back-btn"
                  >
                    Retour au formulaire
                  </button>
                </div>
              </>
            ) : (
              <div className="mt-4 p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/30" data-testid="forgot-sent-message">
                <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                <p className="text-white/80 text-sm">
                  Si un compte existe avec cet email, un nouveau mot de passe vient d'être envoyé.
                  <br /><span className="text-amber-400 text-xs">⚠️ Pensez à vérifier vos spams.</span>
                </p>
                <Button
                  onClick={() => navigate(`/reseau/${type}/login`)}
                  className="mt-4 w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
                  data-testid="forgot-sent-login-btn"
                >
                  Aller à la connexion
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy">
      {/* Header */}
      <header className="py-6 px-6 border-b border-white/10">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/reseau')}
              className="text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="font-heading text-xl font-bold text-white">
              L'ŒIL DU MARCHAND
            </h1>
          </div>
          <span className={`text-${config.color}-400 font-medium`}>
            {config.title.toUpperCase()}
          </span>
        </div>
      </header>

      {/* Form */}
      <main className="p-6">
        <div className="max-w-3xl mx-auto">
          <Card className="border-0 shadow-2xl bg-white/5 backdrop-blur">
            <CardHeader className="border-b border-white/10">
              <CardTitle className="text-white flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg bg-${config.color}-500/20 flex items-center justify-center`}>
                  {type === 'artisan' ? <Wrench className={`w-5 h-5 text-${config.color}-400`} /> :
                   type === 'partenaire' ? <Building2 className={`w-5 h-5 text-${config.color}-400`} /> :
                   <User className={`w-5 h-5 text-${config.color}-400`} />}
                </div>
                <div>
                  <span className="block">{config.title}</span>
                  <span className="text-sm font-normal text-white/60">{config.subtitle}</span>
                </div>
              </CardTitle>
            </CardHeader>
            
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Identité */}
                <div className="space-y-4">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <User className="w-4 h-4" /> Identité
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/70">Prénom</Label>
                      <Input
                        value={formData.prenom}
                        onChange={(e) => handleChange('prenom', e.target.value)}
                        className="bg-white/10 border-white/20 text-white"
                        placeholder="Votre prénom"
                      />
                    </div>
                    <div>
                      <Label className="text-white/70">Nom *</Label>
                      <Input
                        required
                        value={formData.nom}
                        onChange={(e) => handleChange('nom', e.target.value)}
                        className="bg-white/10 border-white/20 text-white"
                        placeholder="Votre nom"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/70">Email *</Label>
                      <Input
                        required
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        className="bg-white/10 border-white/20 text-white"
                        placeholder="email@exemple.com"
                      />
                    </div>
                    <div>
                      <Label className="text-white/70">Téléphone *</Label>
                      <Input
                        required
                        type="tel"
                        value={formData.telephone}
                        onChange={(e) => handleChange('telephone', e.target.value)}
                        className="bg-white/10 border-white/20 text-white"
                        placeholder="06 12 34 56 78"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-white/70">Mot de passe *</Label>
                    <div className="relative">
                      <Input
                        required
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => handleChange('password', e.target.value)}
                        className="bg-white/10 border-white/20 text-white pr-10"
                        placeholder="Créez votre mot de passe"
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Société (optionnel ou obligatoire selon le type) */}
                {!config.requiresCompany && (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="is_company"
                      checked={formData.is_company}
                      onCheckedChange={(checked) => handleChange('is_company', checked)}
                    />
                    <Label htmlFor="is_company" className="text-white/70">
                      Je représente une société (pour facturation TVA)
                    </Label>
                  </div>
                )}

                {(formData.is_company || config.requiresCompany) && (
                  <div className="space-y-4 p-4 bg-white/5 rounded-lg">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                      <Building2 className="w-4 h-4" /> Société
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-white/70">Nom de la société *</Label>
                        <Input
                          required={formData.is_company || config.requiresCompany}
                          value={formData.company_name}
                          onChange={(e) => handleChange('company_name', e.target.value)}
                          className="bg-white/10 border-white/20 text-white"
                          placeholder="SARL / SAS / Auto-entrepreneur..."
                        />
                      </div>
                      <div>
                        <Label className="text-white/70">SIRET *</Label>
                        <Input
                          required={formData.is_company || config.requiresCompany}
                          value={formData.siret}
                          onChange={(e) => handleChange('siret', e.target.value)}
                          className="bg-white/10 border-white/20 text-white"
                          placeholder="123 456 789 00012"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-white/70">Adresse du siège *</Label>
                      <Input
                        required={formData.is_company || config.requiresCompany}
                        value={formData.address}
                        onChange={(e) => handleChange('address', e.target.value)}
                        className="bg-white/10 border-white/20 text-white"
                        placeholder="10 rue de la Paix"
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-white/70">Ville *</Label>
                        <Input
                          required={formData.is_company || config.requiresCompany}
                          value={formData.city}
                          onChange={(e) => handleChange('city', e.target.value)}
                          className="bg-white/10 border-white/20 text-white"
                          placeholder="Paris"
                        />
                      </div>
                      <div>
                        <Label className="text-white/70">Code postal *</Label>
                        <Input
                          required={formData.is_company || config.requiresCompany}
                          value={formData.postal_code}
                          onChange={(e) => handleChange('postal_code', e.target.value)}
                          className="bg-white/10 border-white/20 text-white"
                          placeholder="75001"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Métiers (Artisan) */}
                {config.showMetiers && (
                  <div className="space-y-4">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                      <Wrench className="w-4 h-4" /> Métiers *
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {metiersList.map((metier) => (
                        <button
                          key={metier}
                          type="button"
                          onClick={() => handleMetierToggle(metier)}
                          className={`p-2 rounded-lg text-sm transition-all ${
                            formData.metiers.includes(metier)
                              ? 'bg-orange-500 text-white'
                              : 'bg-white/10 text-white/70 hover:bg-white/20'
                          }`}
                        >
                          {metier}
                        </button>
                      ))}
                    </div>
                    {formData.metiers.includes('Autre') && (
                      <div>
                        <Label className="text-white/70">Précisez votre métier</Label>
                        <Input
                          value={formData.metier_autre}
                          onChange={(e) => handleChange('metier_autre', e.target.value)}
                          className="bg-white/10 border-white/20 text-white"
                          placeholder="Ex: Domoticien, Pisciniste..."
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Zone d'intervention (Artisan) */}
                {config.showZone && (
                  <div className="space-y-4">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> Zone d'intervention *
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-white/70">Ville principale *</Label>
                        <Input
                          required={config.showZone}
                          value={formData.zone_ville}
                          onChange={(e) => handleChange('zone_ville', e.target.value)}
                          className="bg-white/10 border-white/20 text-white"
                          placeholder="Lille, Paris, Lyon..."
                        />
                      </div>
                      <div>
                        <Label className="text-white/70">Rayon d'intervention</Label>
                        <Select
                          value={String(formData.zone_rayon_km)}
                          onValueChange={(value) => handleChange('zone_rayon_km', parseInt(value))}
                        >
                          <SelectTrigger className="bg-white/10 border-white/20 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="20">20 km</SelectItem>
                            <SelectItem value="30">30 km</SelectItem>
                            <SelectItem value="50">50 km</SelectItem>
                            <SelectItem value="75">75 km</SelectItem>
                            <SelectItem value="100">100 km</SelectItem>
                            <SelectItem value="150">150 km</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Documents (Artisan) */}
                {config.showDocuments && (
                  <div className="space-y-4 p-4 bg-white/5 rounded-lg">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                      <FileText className="w-4 h-4" /> Documents
                    </h3>
                    <p className="text-white/60 text-sm">
                      Les documents (Décennale, Kbis, RC Pro) pourront être uploadés après validation de votre compte.
                    </p>
                  </div>
                )}

                {/* Activité (Agence/Autre) */}
                {config.showActivite && (
                  <div className="space-y-4">
                    <h3 className="text-white font-semibold">Votre activité *</h3>
                    <Input
                      required
                      value={formData.activite}
                      onChange={(e) => handleChange('activite', e.target.value)}
                      className="bg-white/10 border-white/20 text-white"
                      placeholder="Agence immobilière, Courtier, Architecte..."
                    />
                  </div>
                )}

                {/* Ville de résidence / d'activité (pour agence, affilié, partenaire) */}
                {config.showVille && !config.showZone && (
                  <div className="space-y-4">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> Ville principale *
                    </h3>
                    <div>
                      <Input
                        required
                        value={formData.zone_ville}
                        onChange={(e) => handleChange('zone_ville', e.target.value)}
                        className="bg-white/10 border-white/20 text-white"
                        placeholder="Paris, Lyon, Marseille..."
                      />
                      <p className="text-white/50 text-xs mt-1">
                        Votre ville principale — utilisée pour vous localiser sur la carte réseau.
                      </p>
                    </div>
                  </div>
                )}

                {/* Modèle Partenaire */}
                {config.showPartnerModel && (
                  <div className="space-y-4">
                    {/* Sélecteur de pays — 2 niveaux */}
                    <div className="space-y-3">
                      <h3 className="text-white font-semibold flex items-center gap-2">Pays d'activité *</h3>

                      {/* Niveau 1 : France ou Étranger */}
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { key: 'france',   flag: '🇫🇷', label: 'France',   sub: '100 €/mois' },
                          { key: 'etranger', flag: '🌍',   label: 'Étranger', sub: 'Sélectionner' },
                        ].map(z => {
                          const currentZone = ['france'].includes(formData.country) ? 'france' : 'etranger';
                          const isSelected = currentZone === z.key;
                          return (
                            <button key={z.key} type="button"
                              onClick={() => handleChange('country', z.key === 'france' ? 'france' : 'algerie')}
                              className={`p-3 rounded-lg border-2 text-left transition-all ${isSelected ? 'border-purple-500 bg-purple-500/20' : 'border-white/20 bg-white/5 hover:border-white/40'}`}
                            >
                              <div className="text-2xl mb-1">{z.flag}</div>
                              <div className="text-white font-medium text-sm">{z.label}</div>
                              <div className="text-white/50 text-xs">{z.sub}</div>
                            </button>
                          );
                        })}
                      </div>

                      {/* Niveau 2 : liste des pays étrangers */}
                      {formData.country !== 'france' && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {[
                            { key: 'algerie', flag: '🇩🇿', label: 'Algérie',  currency: 'DZD', price: '15 000 DZD/mois', done: false },
                            { key: 'maroc',   flag: '🇲🇦', label: 'Maroc',    currency: 'MAD', price: '1 000 MAD/mois',  done: false },
                            { key: 'tunisie', flag: '🇹🇳', label: 'Tunisie',  currency: 'TND', price: '300 TND/mois',    done: false },
                            { key: 'senegal', flag: '🇸🇳', label: 'Sénégal',  currency: 'XOF', price: '65 000 CFA/mois', done: false },
                            { key: 'autre',   flag: '🌍',   label: 'Autre pays', currency: '—', price: 'Prix perso',       done: false },
                          ].map(p => {
                            const isSelected = formData.country === p.key;
                            return (
                              <button key={p.key} type="button"
                                onClick={() => handleChange('country', p.key)}
                                className={`relative p-3 rounded-lg border-2 text-left transition-all ${isSelected ? 'border-emerald-500 bg-emerald-500/20' : 'border-white/20 bg-white/5 hover:border-white/40'} ${!p.done ? 'opacity-70' : ''}`}
                              >
                                {!p.done && <span className="absolute top-1.5 right-1.5 text-[9px] bg-amber-500/80 text-white px-1 py-0.5 rounded">Bientôt</span>}
                                <div className="text-xl mb-1">{p.flag}</div>
                                <div className="text-white font-medium text-xs">{p.label}</div>
                                <div className="text-white/40 text-[10px]">{p.price}</div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <h3 className="text-white font-semibold">Modèle choisi *</h3>

                    <div className="p-4 rounded-lg border-2 border-purple-500 bg-purple-500/10">
                      <p className="text-white font-semibold">Page Blanche (Marque Blanche)</p>
                      <p className="text-white/60 text-sm">{formData.country === 'france' ? '100€/mois — sans engagement' : formData.country === 'algerie' ? '15 000 DZD/mois — sans engagement' : 'Tarif selon votre pays — sans engagement'} — Votre marque, votre logo, votre espace complet</p>
                    </div>

                    {/* Détails Page Blanche après sélection */}
                    {formData.partner_model === 'page_blanche' && (
                      <div className="mt-4 p-4 bg-purple-900/30 rounded-lg border border-purple-500/30">
                        <h4 className="text-purple-300 font-semibold mb-3">Informations pour votre Page Blanche :</h4>
                        
                        {/* Sous-domaine souhaité avec EXEMPLE CONCRET */}
                        <div className="mb-4">
                          <Label className="text-white/80 mb-2 block">Nom de votre page personnalisée *</Label>
                          <div className="p-3 bg-white/5 rounded mb-3 border border-white/10">
                            <p className="text-white/70 text-sm mb-2">
                              <strong className="text-purple-300">Comment ça marche ?</strong> Vos clients accéderont à votre page via cette adresse. 
                              C'est le nom qui apparaîtra dans l'URL.
                            </p>
                            <p className="text-white/60 text-xs">
                              <strong>Exemple :</strong> Si vous êtes <span className="text-purple-300">"Agence du Golfe"</span>, 
                              tapez <span className="text-purple-300 font-mono">agence-du-golfe</span>
                            </p>
                            <p className="text-white/60 text-xs mt-1">
                              → Vos clients iront sur : <span className="text-purple-300 font-mono">oeil-marchand.com/p/agence-du-golfe</span>
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-white/50 text-sm whitespace-nowrap">oeil-marchand.com/p/</span>
                            <Input
                              value={formData.subdomain || ''}
                              onChange={(e) => handleChange('subdomain', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                              className="bg-white/10 border-white/20 text-white flex-1"
                              placeholder="agence-du-golfe"
                            />
                          </div>
                          <p className="text-white/50 text-xs mt-1">
                            Lettres minuscules, chiffres et tirets uniquement (pas d'espaces, pas d'accents)
                          </p>
                        </div>

                        {/* Upload Logo - AVEC UPLOAD DIRECT */}
                        <div className="mb-4">
                          <Label className="text-white/80 mb-2 block flex items-center gap-2">
                            <Camera className="w-4 h-4" /> Votre logo (optionnel)
                          </Label>
                          <div className="p-3 bg-white/5 rounded mb-3 border border-white/10">
                            <p className="text-white/70 text-sm">
                              <strong className="text-purple-300">Uploadez votre logo</strong> directement ou collez un lien.
                              Il apparaîtra sur votre page personnalisée.
                            </p>
                          </div>
                          
                          {/* Logo Preview */}
                          {logoPreview && (
                            <div className="mb-3 relative inline-block">
                              <img 
                                src={logoPreview} 
                                alt="Logo preview" 
                                className="h-20 w-auto object-contain bg-white rounded-lg p-2"
                              />
                              <button
                                type="button"
                                onClick={removeLogo}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                          
                          {/* Upload Button */}
                          {!logoPreview && (
                            <div className="flex gap-3 mb-3">
                              <input
                                ref={logoInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleLogoSelect}
                                className="hidden"
                                id="logo-upload"
                              />
                              <label 
                                htmlFor="logo-upload"
                                className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg cursor-pointer transition-colors"
                              >
                                {uploadingLogo ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Upload className="w-4 h-4" />
                                )}
                                Choisir une image
                              </label>
                              <span className="text-white/50 text-sm self-center">ou</span>
                            </div>
                          )}
                          
                          {/* URL Input (alternative) */}
                          {!logoPreview && (
                            <Input
                              type="url"
                              value={formData.logo_url || ''}
                              onChange={(e) => handleChange('logo_url', e.target.value)}
                              className="bg-white/10 border-white/20 text-white"
                              placeholder="https://exemple.com/mon-logo.png"
                            />
                          )}
                          <p className="text-white/50 text-xs mt-1">
                            Formats acceptés : JPG, PNG, GIF, WebP, SVG (max 5MB)
                          </p>
                        </div>
                        
                        {/* Couleur de marque */}
                        <div className="mb-4">
                          <Label className="text-white/80 mb-2 block flex items-center gap-2">
                            <Palette className="w-4 h-4" /> Couleur de votre marque
                          </Label>
                          <div className="p-3 bg-white/5 rounded mb-3 border border-white/10">
                            <p className="text-white/70 text-sm">
                              Cette couleur sera utilisée pour les boutons et accents sur votre page.
                            </p>
                          </div>
                          
                          {/* Color Picker */}
                          <div className="flex flex-wrap gap-2 mb-3">
                            {brandColors.map((color) => (
                              <button
                                key={color.value}
                                type="button"
                                onClick={() => handleChange('primary_color', color.value)}
                                className={`w-10 h-10 rounded-lg transition-all ${
                                  formData.primary_color === color.value 
                                    ? 'ring-2 ring-white ring-offset-2 ring-offset-purple-900 scale-110' 
                                    : 'hover:scale-105'
                                }`}
                                style={{ backgroundColor: color.value }}
                                title={color.name}
                              />
                            ))}
                            {/* Custom color input */}
                            <div className="relative">
                              <input
                                type="color"
                                value={formData.primary_color}
                                onChange={(e) => handleChange('primary_color', e.target.value)}
                                className="w-10 h-10 rounded-lg cursor-pointer border-2 border-white/20"
                                title="Couleur personnalisée"
                              />
                            </div>
                          </div>
                          
                          {/* Preview */}
                          <div className="p-3 rounded-lg border border-white/10" style={{ backgroundColor: formData.primary_color + '20' }}>
                            <p className="text-white/70 text-sm mb-2">Aperçu :</p>
                            <button 
                              type="button"
                              className="px-4 py-2 text-white rounded-lg text-sm font-medium"
                              style={{ backgroundColor: formData.primary_color }}
                            >
                              Bouton exemple
                            </button>
                          </div>
                        </div>

                        <div className="p-3 bg-purple-800/30 rounded text-sm text-white/70">
                          <p className="font-medium text-purple-300 mb-1">Après validation et paiement :</p>
                          <p>Vous recevrez vos codes d'accès à votre dashboard partenaire par email. Une hotline sera disponible pour toute question.</p>
                          <p className="text-amber-300 text-xs mt-2">⚠️ Pensez à vérifier vos spams si vous ne recevez rien.</p>
                        </div>
                      </div>
                    )}

                    {/* Info Commission après sélection */}
                    {formData.partner_model === 'commission' && null}
                  </div>
                )}

                {/* Submit */}
                <Button
                  type="submit"
                  disabled={loading}
                  className={`w-full bg-${config.color}-500 hover:bg-${config.color}-600 text-white font-semibold py-6`}
                >
                  {loading ? 'Inscription en cours...' : 'S\'inscrire'}
                </Button>

                <p className="text-white/50 text-xs text-center">
                  En vous inscrivant, vous acceptez nos conditions générales d'utilisation.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
